import { cn } from '@/lib/utils';

import type { FreshStrawTripContext } from '../../hooks/useFreshStrawTripContext';

/**
 * Read-only display banner shown when the fresh-straw page is launched from
 * service-scheduler with `?tripId=…&stopId=…`. Purely informational — the
 * filter panel below is still where the user picks the site and account
 * Guids (the service-scheduler stop stores those as short string codes, not
 * Guids, so we can't auto-resolve them).
 *
 * Render contract: if the trip context is empty, returns `null` so callers
 * can unconditionally mount the component and let it decide whether to show.
 */

export interface FreshStrawTripBannerProps {
  context: FreshStrawTripContext;
  tripId?: string;
  stopId?: string;
  className?: string;
}

export function FreshStrawTripBanner({
  context,
  tripId,
  stopId,
  className,
}: FreshStrawTripBannerProps) {
  if (context.error) {
    return (
      <div
        role="alert"
        className={cn(
          'border-destructive/40 bg-destructive/10 text-destructive rounded-lg border px-3 py-2 text-xs',
          className,
        )}
        data-slot="fresh-straw-trip-banner"
      >
        Could not load the scheduled trip. Pick the transfer site, account, and
        date manually below.
      </div>
    );
  }

  if (context.isLoading) {
    return (
      <div
        className={cn(
          'text-muted-foreground rounded-lg border px-3 py-2 text-xs',
          className,
        )}
        data-slot="fresh-straw-trip-banner"
      >
        Loading trip details…
      </div>
    );
  }

  if (!context.trip || !context.stop) return null;

  const fields: Array<{ label: string; value: string | undefined }> = [
    { label: 'Shipment', value: context.shipmentNumber },
    { label: 'Site code', value: context.siteCodeHint },
    { label: 'Start', value: context.transferDateFromStop },
    { label: 'Account', value: context.accountNumberHint },
    { label: 'Vet', value: context.psVetId },
    { label: 'Embryologist', value: context.psEmbryologistId },
  ];

  return (
    <section
      className={cn('bg-muted/30 rounded-lg border px-3 py-2', className)}
      data-slot="fresh-straw-trip-banner"
      aria-label="Scheduled trip context"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="text-muted-foreground font-semibold uppercase">
          Trip context
        </span>
        {tripId && stopId && (
          <span className="text-muted-foreground rounded border px-2 py-0.5 font-mono text-[11px]">
            {tripId.slice(0, 8).toUpperCase()} · Stop{' '}
            {stopId.slice(0, 8).toUpperCase()}
          </span>
        )}
        {fields
          .filter((f) => f.value)
          .map((f) => (
            <span key={f.label} className="flex items-baseline gap-1">
              <span className="text-muted-foreground">{f.label}:</span>
              <span className="text-foreground font-medium tabular-nums">
                {f.value}
              </span>
            </span>
          ))}
      </div>
      {context.comments && (
        <p className="text-muted-foreground mt-1 text-xs italic">
          “{context.comments}”
        </p>
      )}
      <p className="text-muted-foreground mt-1 text-[11px]">
        Site code and account are display-only hints — pick the matching site
        and account below.
      </p>
    </section>
  );
}
